<template>
  <article class="navigation">
    <div
      class="navigation__backing"
      v-if="sidebarOpen"
      @click="closeAllBacking"
    ></div>
    <div class="navigation__base">
      <div
        :class="[
          'navigation__left',
          { 'navigation__left--hidden': routeCheck },
        ]"
      >
        <IconButton label="home" icon="home" is-column @action="goLanding" />

        <template v-for="link of topLinks">
          <div
            v-if="$route.name !== link.route"
            :key="link.route"
            :class="[
              'navigation__link',
              link.cssClass && `navigation__${link.cssClass}`,
              {
                navigation__link__disabled:
                  link.enabled != null && !link.enabled,
              },
            ]"
            @click="(link.enabled == null || link.enabled) && navigateTo(link)"
          >
            <span>{{ link.globalNavLabel || link.label }}</span>
          </div>
        </template>

        <div
          v-if="!isRouteInTopLinks(this.$route.name)"
          class="navigation__circle"
          @click="toggleSideBar()"
        >
          <div
            :class="[
              'navigation__menu',
              { 'navigation__menu--open': sidebarOpen },
            ]"
          >
            <span>MENU</span>
          </div>
        </div>

        <IconButton
          label="glossary"
          icon="glossary"
          is-column
          @action="getCurrentModal('Glossary')"
        />
        <IconButton
          label="references"
          icon="list"
          is-column
          @action="getCurrentModal('References')"
        />
      </div>

      <div
        :class="[
          'navigation__right',
          {
            'navigation__right--hidden': routeCheck,
          },
        ]"
      >
        <div class="navigation__head">
          <TooltipMenu
            :tooltip-list="tooltipList"
            :active-item="activeItem"
            @action="($event) => $emit('emit-active-nav', $event)"
          />

          <Logo />
        </div>

        <slot name="app-slot" />
        <slot name="footer-slot" />
      </div>
    </div>

    <div
      :class="[
        'navigation__sidebar',
        {
          'navigation__sidebar--open': sidebarOpen,
        },
      ]"
    >
      <SvgClose class="navigation__close" @click="sidebarOpen = false" />
      <h3 class="navigation__title">{{ activeItem.label }}</h3>
      <h5 class="navigation__subtitle">Select up to 6 tiles at a time</h5>

      <div>
        <div
          ref="menuListing"
          class="menu-listing"
          @scroll.passive="updateShadow($event)"
        >
          <List
            :list="getModel[0] ? getModel[0].sidebar : []"
            @emit-active-sub="getDataList($event, getModel[0].id)"
            @emit-remove-active-item="removeActiveItem($event)"
          />
        </div>

        <Button
          label="Clear All"
          is-fluid
          :is-clear="activeDataList.length > 0"
          :is-disabled="activeDataList.length === 0"
          @action="clearActiveSub"
        />
      </div>
    </div>
  </article>
</template>

<script>
// Plugins
import ClickOutside from "vue-click-outside";
import scrollShadowMixin from "@/mixins/scrollShadowMixin";

// Vuex
import { mapGetters, mapActions } from "vuex";

export default {
  name: "Navigation",

  mixins: [scrollShadowMixin],

  components: {
    Button: () => import("./Button.vue"),
    List: () => import("./List.vue"),
    Logo: () => import("./Logo.vue"),
    TooltipMenu: () => import("./TooltipMenu.vue"),
    IconButton: () => import("./IconButton.vue"),
    //
    SvgClose: () => import("@/assets/svgs/close.svg?inline"),
  },

  directives: {
    ClickOutside,
  },

  props: {
    tooltipList: {
      type: Array,
      default: () => [],
    },
    activeItem: {
      type: Object,
      default: () => {
        return {
          label: "Active",
          value: "active",
        };
      },
    },
    topLinks: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    sidebarOpen: false,
  }),

  computed: {
    ...mapGetters([
      "activeRoute",
      "activeDataList",
      "activeCard",
      "activeIndex",
      "maxListLength",
    ]),

    routeCheck() {
      return (
        this.$route.name === "landing" || this.$route.name === "style-guide"
      );
    },

    getModel() {
      return this.data.filter((item) => item.id === this.activeItem.value);
    },
  },

  methods: {
    ...mapActions([
      "removeFromActiveDataList",
      "getActiveDataList",
      "clearActiveDataList",
      "getActiveCard",
      "updateActiveIndex",
      "getCurrentModal",
    ]),

    isRouteInTopLinks(route) {
      return this.topLinks.find((link) => link.route === route);
    },

    updateShadow($event) {
      requestAnimationFrame(() => {
        const element = $event.target;
        const limit = element.scrollHeight - 5;
        const scrolled = element.offsetHeight + element.scrollTop;
        element.style.setProperty(
          "--top-shadow-opacity",
          element.scrollTop === 0 ? 0 : 1
        );
        element.style.setProperty(
          "--bottom-shadow-opacity",
          scrolled >= limit ? 0 : 1
        );
      });
    },

    goLanding() {
      this.sidebarOpen = false;
      this.$router.push({
        name: "landing",
      });
    },

    navigateTo(item) {
      this.sidebarOpen = false;
      this.$emit("emit-active-nav", item);
      this.$router.push({ name: item.route, params: item });
    },

    toggleSideBar() {
      this.sidebarOpen = !this.sidebarOpen;

      // We need to trigger a scroll event to cause the effects to reflect the need for scroll.
      this.refreshScrollEffect(this.$refs.menuListing);
    },

    closeAllBacking() {
      this.sidebarOpen = false;
    },

    async getDataList(e, id) {
      const check = this.activeDataList.length < this.maxListLength;
      e.content = e.content.map((entry) => {
        const dataset = this.$chartDataService.getChart(id, entry.id);

        entry.data = dataset.data;
        entry.dataSrc = dataset.data;
        entry.config = dataset.config;

        entry.config.legend = dataset.config.chartLegend?.map((l) => ({
          active: true,
          entries: l.entries,
        }));

        return entry;
      });

      if (check) {
        await this.getActiveDataList(e);
        await this.getActiveCard(
          this.activeDataList[this.activeDataList.length - 1]
        );

        const index = this.activeDataList.findIndex(
          (i) => i.tileId === this.activeCard.tileId
        );

        this.updateActiveIndex(index);
      }
    },

    clearActiveSub() {
      if (this.activeDataList.length > 0) {
        this.clearActiveDataList();
        this.updateActiveIndex(0);
      }
    },

    removeActiveItem(subitem) {
      //get index of target element
      let targetIndex = this.activeDataList.findIndex(
        (x) => x.tileId === subitem.tileId
      );

      //remove from active chart list based on target index
      this.getActiveCard(this.activeDataList[targetIndex]);
      this.removeFromActiveDataList(this.activeCard);
      if (this.activeDataList.length !== 0) {
        //move user to end of active data listing
        this.updateActiveIndex(this.activeDataList.length - 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.navigation {
  position: relative !important;
  overflow: hidden !important;
  z-index: 0 !important;

  &__backing {
    width: 100%;
    height: 100%;
    position: fixed;
    z-index: 10;
  }

  &__base {
    display: flex;
    z-index: 11;
  }

  &__right {
    width: 100%;

    &--hidden {
      margin-top: rem(-80);
    }
  }

  &__head {
    background: $globalNavHeaderBG;
    padding: rem(24) 0;
    @include flexbox(center, center);
    height: rem(80);
    position: relative;
    width: 100%;

    .logo {
      height: rem(25);
      position: absolute;
      right: rem(45);
    }
  }

  &__left {
    height: 100vh;
    min-height: rem(768);
    background: $leftNavBG;
    min-width: rem(154);
    padding: 80px 25px;
    @include flexbox(center, center);
    flex-direction: column;
    z-index: 99;

    &--hidden {
      margin-left: rem(-154);
    }

    > * {
      border-top: 0.5px solid rgba(255, 255, 255, 0.4) !important;
      width: 100%;
      cursor: pointer;
      height: rem(120);

      &:last-child {
        border-bottom: 1px solid rgba(255, 255, 255, 0.4);
      }
    }
  }

  &__circle,
  &__link,
  &__moa {
    @include flexbox(center, center);
  }

  &__link {
    font-weight: $font-bold;
    text-align: center;
    min-height: 108px;
    line-height: 16px;

    span {
      color: $white;
      font-size: rem(14);
    }

    &__disabled span {
      color: rgba($white, 0.6);
    }
  }

  &__menu {
    border-radius: 50%;
    width: rem(70);
    height: rem(70);
    border: 1px solid $white;
    @include flexbox(center, center);

    span {
      color: $white;
      font-size: rem(14);
      font-weight: $font-bold;
    }

    &--open {
      background: #006d8a;
    }
  }

  &__sidebar {
    height: 100%;
    position: absolute;
    width: rem(350);
    border-radius: 0 15px 15px 0;
    background: $white;
    top: 0;
    left: rem(-350);
    z-index: 0;
    padding: rem(25) rem(0);
    z-index: 9;
    box-shadow: 10px 0 52px -31px rgba(0, 0, 0, 0.75);

    &--open {
      left: rem(154);
      z-index: 11;
    }

    .button {
      position: absolute;
      bottom: rem(20);
      left: rem(15);
      max-width: rem(320);
    }
  }

  &__close {
    width: rem(20);
    position: absolute;
    right: rem(30);
    top: rem(30);
    cursor: pointer;
    z-index: 1;
  }

  &__title {
    color: $globalSideNavHeaderColour;
    font-size: rem(25);
    line-height: 1.25;
    font-weight: $font-bold;
    padding-left: rem(12);
  }

  &__subtitle {
    padding-left: rem(12);
    margin-bottom: rem(20);
    font-size: rem(14);
    font-weight: 400;
    color: #9b9b9b;
  }
}

.menu-listing {
  --top-shadow-opacity: 0;
  --bottom-shadow-opacity: 0;
  overflow: auto;
  position: absolute;
  height: calc(100% - 182px);
  width: 100%;

  &:after,
  &:before {
    content: "";
    display: block;
    height: 30px;
    position: sticky;
    pointer-events: none;
    transition: opacity linear 0.15s;
  }

  &:before {
    top: 0;
    margin-bottom: -30px;
    background: linear-gradient(rgba(#fff, 1), rgba(#fff, 0));
    opacity: var(--top-shadow-opacity);
  }

  &:after {
    bottom: 0;
    margin-top: -30px;
    background: linear-gradient(rgba(#fff, 0), rgba(#fff, 1));
    opacity: var(--bottom-shadow-opacity);
  }

  .list {
    margin-bottom: 0;
  }
}

@include iPadProStyles {
  .navigation {
    &__right {
      &--hidden {
        margin-top: rem(-105);
      }
    }

    &__head {
      height: rem(105);

      .logo {
        height: rem(35);
      }
    }

    &__left {
      min-width: rem(200);
      padding: 105px 25px 80px 25px;

      &--hidden {
        margin-left: rem(-200);
      }

      > * {
        height: rem(152);
      }
    }

    &__link {
      line-height: rem(20);

      span {
        font-size: rem(20);
      }
    }

    &__menu {
      width: rem(90);
      height: rem(90);

      span {
        font-size: rem(20);
      }
    }

    &__close {
      width: rem(30);
    }

    &__title {
      font-size: rem(30);
    }

    &__subtitle {
      font-size: rem(20);
    }

    &__sidebar {
      padding: rem(25) rem(15);
      width: rem(450);
      left: rem(-450);

      &--open {
        left: rem(200);
      }

      .button {
        max-width: rem(420);
      }
    }
  }

  .menu-listing {
    width: calc(100% - #{rem(15 * 2)});
    height: calc(100% - 200px);
  }
}
</style>
